import { analyticsAllowed, clearAnalyticsStorage } from "./analyticsConsent";

let client;
let loading;
let generation = 0;
let lastPage;
const TOKEN = process.env.REACT_APP_POSTHOG_PROJECT_TOKEN;
const HOST = process.env.REACT_APP_POSTHOG_HOST;
const PUBLIC_PAGES = new Set(["/", "/early-access", "/story", "/journal", "/ingredients", "/preview", "/preview/shop", "/preview/cart"]);

// Never transmit arbitrary paths (which may themselves contain contact details or IDs).
export function analyticsPage(pathname) {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (PUBLIC_PAGES.has(path)) return path;
  if (path.startsWith("/ingredients/")) return "/ingredients/:slug";
  if (path.startsWith("/journal/")) return "/journal/:slug";
  if (path.startsWith("/preview/product/")) return "/preview/product/:handle";
  return null; // Sample, feedback, investor, account, checkout, legal and unknown routes excluded.
}

function safeEvent(event) {
  const page = analyticsPage(window.location.pathname);
  if (!analyticsAllowed() || event.event !== "$pageview" || !page) return null;
  return {
    event: "$pageview", uuid: event.uuid, timestamp: event.timestamp,
    properties: {
      token: TOKEN, distinct_id: event.properties.distinct_id,
      $process_person_profile: false, $geoip_disable: true,
      $pathname: page, $current_url: `${window.location.origin}${page}`,
    },
  };
}

async function startAnalytics() {
  if (!analyticsAllowed()) return null;
  if (!TOKEN || !HOST) throw new Error("Analytics configuration is missing");
  if (client) return client;
  if (loading) return loading;
  const revision = generation;
  loading = import("posthog-js/dist/module.no-external").then(({ default: posthog }) => {
    if (revision !== generation || !analyticsAllowed()) return null;
    clearAnalyticsStorage();
    client = posthog.init(TOKEN, {
      api_host: HOST, person_profiles: "never", persistence: "memory", disable_persistence: true,
      opt_out_capturing_by_default: false, opt_out_persistence_by_default: false,
      autocapture: false, capture_pageview: false, capture_pageleave: false,
      capture_dead_clicks: false, rageclick: false, capture_heatmaps: false,
      capture_exceptions: false, capture_performance: false, disable_scroll_properties: true,
      disable_session_recording: true, enable_recording_console_log: false,
      disable_surveys: true, disable_product_tours: true, disable_conversations: true,
      disable_external_dependency_loading: true, disableDeviceModel: true,
      advanced_disable_flags: true, remote_config_refresh_interval_ms: 0,
      advanced_disable_toolbar_metrics: true, opt_in_site_apps: false,
      mask_all_text: true, mask_all_element_attributes: true,
      session_recording: { maskAllInputs: true, maskTextSelector: "*", blockSelector: "form, .ph-no-capture", recordCrossOriginIframes: false, recordHeaders: false, recordBody: false },
      save_referrer: false, store_google: false, disable_campaign_params: true,
      request_batching: false, before_send: safeEvent,
      get_current_url: () => `${window.location.origin}${analyticsPage(window.location.pathname) || "/"}`,
    }, `vituraConsentAnalytics${revision}`);
    return client;
  }).finally(() => { loading = null; });
  return loading;
}

export async function trackPage() {
  const page = analyticsPage(window.location.pathname);
  if (!page || !analyticsAllowed()) { lastPage = null; return; }
  const ph = await startAnalytics();
  if (!ph || !analyticsAllowed() || analyticsPage(window.location.pathname) !== page || lastPage === page) return;
  lastPage = page;
  ph.capture("$pageview", { $pathname: page });
}

export function stopAnalytics() {
  generation += 1;
  lastPage = null;
  const previous = client;
  client = undefined;
  if (previous) {
    previous.stopSessionRecording();
    previous.opt_out_capturing();
    void previous.shutdown();
  }
  clearAnalyticsStorage();
}